// The agreement-terms timeline — the screen the data model exists for.
//
// §07: an escalator or a step-down is not an edit to a term, it is an
// ADDITIONAL row with a future effective date. The term in force is the latest
// row whose effective_date <= today. Rendering that as one "current split"
// number would hide the two things a reviewer comes here to check: what the
// split used to be, and what it is about to become. So every term is shown,
// newest-effective first, with the one in force and any not-yet-effective row
// each called out explicitly.

import type { ReactNode } from "react";
import { splitMinor } from "@crm/shared";
import type { AgreementImplFeeTier, AgreementTermWithTiers, AgreementWithTerms } from "@crm/shared";
import styles from "./AgreementTermsTimeline.module.css";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Notice } from "../../design/Feedback.js";
import { Table, Td, Th } from "../../design/Table.js";
import { formatCalendarDate, todayIsoDate } from "../../format/calendarDate.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";

/** Where a term sits relative to today. Drives the badge and the emphasis. */
type TermStanding = "in_force" | "future" | "superseded";

function standingOf(
  term: AgreementTermWithTiers,
  currentTermId: string | null,
  today: string,
): TermStanding {
  if (term.id === currentTermId) return "in_force";
  // ISO calendar dates compare lexicographically — no Date, no timezone, no
  // chance of a term flipping status for a reader west of UTC.
  return term.effective_date > today ? "future" : "superseded";
}

interface StandingBadge {
  tone: Tone;
  label: string;
}

function standingBadge(standing: TermStanding): StandingBadge {
  switch (standing) {
    case "in_force":
      return { tone: "good", label: "In force today" };
    case "future":
      return { tone: "info", label: "Scheduled — not yet in effect" };
    case "superseded":
      return { tone: "neutral", label: "Superseded" };
    default:
      return assertNever(standing, "term standing");
  }
}

export interface AgreementTermsTimelineProps {
  agreement: AgreementWithTerms;
  /** Injectable so the "today" boundary is testable. */
  today?: string;
  /**
   * Editing is opt-in: omitted (no `crm_editor` role, or a read-only context)
   * the timeline renders exactly as it did before, with no inert controls.
   */
  onEditTerm?: (term: AgreementTermWithTiers) => void;
  onDeleteTerm?: (term: AgreementTermWithTiers) => void;
  /**
   * Implementation-fee band editing, opt-in separately from term editing.
   *
   * Separate because the two are different jobs: the split percentages are
   * negotiated once and the fee schedule is the thing that grows a band every
   * time a bigger customer turns up. A screen may legitimately offer one and not
   * the other, and folding them into a single `canEdit` would put an "Add band"
   * button on a term that prices no implementation fee at all.
   */
  onAddTier?: (term: AgreementTermWithTiers) => void;
  onEditTier?: (term: AgreementTermWithTiers, tier: AgreementImplFeeTier) => void;
  onDeleteTier?: (term: AgreementTermWithTiers, tier: AgreementImplFeeTier) => void;
}

/**
 * A band's ceiling, in the words the agreement uses.
 *
 * "Up to 75,000" and "Over 75,000" rather than "75000" and "null": the ceiling
 * is INCLUSIVE and the open-ended band has no number at all, and a column of
 * bare integers with one empty cell says neither of those things. The top band
 * is described by the band below it, which is exactly how the schedule works.
 */
function bandLabel(tier: AgreementImplFeeTier, previousCeiling: number | null): string {
  if (tier.max_accounts === null) {
    return previousCeiling === null
      ? "Any number of accounts"
      : `Over ${formatCount(previousCeiling)} accounts`;
  }
  const upTo = `Up to ${formatCount(tier.max_accounts)} accounts`;
  return previousCeiling === null
    ? upTo
    : `${formatCount(previousCeiling + 1)}–${formatCount(tier.max_accounts)} accounts`;
}

export function AgreementTermsTimeline({
  agreement,
  today = todayIsoDate(),
  onEditTerm,
  onDeleteTerm,
  onAddTier,
  onEditTier,
  onDeleteTier,
}: AgreementTermsTimelineProps): ReactNode {
  const currentTermId = agreement.current_term?.id ?? null;
  const canEdit = onEditTerm !== undefined || onDeleteTerm !== undefined;
  const canEditTiers =
    onAddTier !== undefined || onEditTier !== undefined || onDeleteTier !== undefined;
  // The bands column earns its place only when some term actually prices an
  // implementation fee, or when a reader could add one. Most agreements in this
  // CRM band nothing, and an always-present column of "—" would push the
  // percentages — the thing the timeline is for — off the side of the table.
  const showTiers = canEditTiers || agreement.terms.some((term) => term.impl_fee_tiers.length > 0);

  // The API returns terms newest-effective first; sorting again keeps that true
  // regardless, because this screen's whole claim is "newest first".
  const terms = [...agreement.terms].sort((a, b) =>
    a.effective_date < b.effective_date ? 1 : a.effective_date > b.effective_date ? -1 : 0,
  );

  if (terms.length === 0) {
    return (
      <div className={styles.notice}>
        <Notice
          tone="warn"
          title="This agreement has no terms, so no split can be resolved for its deals."
        >
          A report touching them will fail with <code className="mono">split_unresolvable</code>{" "}
          rather than guess.
        </Notice>
      </div>
    );
  }

  return (
    <>
      {currentTermId === null && (
        <div className={styles.notice}>
          <Notice
            tone="warn"
            title="No term is in force today — every term below is future-dated."
          />
        </div>
      )}

      <Table label={`Terms of ${agreement.name}, newest effective date first`}>
        <thead>
          <tr>
            <Th>Effective date</Th>
            <Th numeric>Impl split</Th>
            <Th numeric>Recurring split</Th>
            <Th numeric>Cost split</Th>
            <Th>Status</Th>
            {canEdit && <Th />}
          </tr>
        </thead>
        <tbody>
          {terms.flatMap((term) => {
            const badge = standingBadge(standingOf(term, currentTermId, today));
            const rows: ReactNode[] = [];
            rows.push(
              <tr key={term.id}>
                <Td mono nowrap>
                  {formatCalendarDate(term.effective_date)}
                </Td>
                <Td numeric mono>
                  {formatPercent(term.impl_split_pct)}
                </Td>
                <Td numeric mono>
                  {formatPercent(term.recurring_split_pct)}
                </Td>
                {term.cost_split_pct === null ? (
                  // Null and 0 are different statements: "cost splitting isn't
                  // part of this agreement" vs "we bear none of it".
                  <Td numeric muted className={styles.absent}>
                    not part of this agreement
                  </Td>
                ) : (
                  <Td numeric mono>
                    {formatPercent(term.cost_split_pct)}
                  </Td>
                )}
                <Td>
                  <Badge shape="chip" tone={badge.tone}>
                    {badge.label}
                  </Badge>
                </Td>
                {canEdit && (
                  <Td>
                    <div className={styles.rowActions}>
                      {onEditTerm !== undefined && (
                        <Button
                          small
                          variant="subtle"
                          aria-label={`Edit the term effective ${term.effective_date}`}
                          onClick={() => onEditTerm(term)}
                        >
                          Edit
                        </Button>
                      )}
                      {onDeleteTerm !== undefined && (
                        <Button
                          small
                          variant="subtle"
                          aria-label={`Delete the term effective ${term.effective_date}`}
                          onClick={() => onDeleteTerm(term)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </Td>
                )}
              </tr>,
            );

            // The bands, as a sub-row rather than a sixth column. A term prices
            // between zero and a handful of them, and squeezing a list into a
            // cell would either truncate the schedule or make every row as tall
            // as the longest one. Spanning the table keeps the percentages
            // readable and gives the schedule the width it needs.
            if (showTiers) {
              rows.push(
                <tr key={`${term.id}-tiers`} className={styles.tierRow}>
                  <td colSpan={canEdit ? 6 : 5}>
                    <ImplFeeSchedule
                      term={term}
                      {...(onAddTier === undefined ? {} : { onAdd: () => onAddTier(term) })}
                      {...(onEditTier === undefined
                        ? {}
                        : { onEdit: (tier: AgreementImplFeeTier) => onEditTier(term, tier) })}
                      {...(onDeleteTier === undefined
                        ? {}
                        : { onDelete: (tier: AgreementImplFeeTier) => onDeleteTier(term, tier) })}
                    />
                  </td>
                </tr>,
              );
            }

            return rows;
          })}
        </tbody>
      </Table>
    </>
  );
}

interface ImplFeeScheduleProps {
  term: AgreementTermWithTiers;
  onAdd?: () => void;
  onEdit?: (tier: AgreementImplFeeTier) => void;
  onDelete?: (tier: AgreementImplFeeTier) => void;
}

/**
 * One term's implementation-fee schedule: the buy-in bands, cheapest ceiling
 * first, with our share of each spelled out beside the gross.
 *
 * Our share is shown BECAUSE the gross alone is the wrong number to reason
 * about. The agreement quotes $45,000 and the term keeps 75% of it — a reader
 * checking "what is this band worth to us" against Attachment B would otherwise
 * have to do the multiplication in their head, using a percentage from a
 * different column of the same row.
 *
 * A declared currency with no bands is a real and distinguishable state:
 * somebody set the currency and has not entered the schedule yet. Saying so is
 * what turns an empty section into a next action rather than a shrug.
 */
function ImplFeeSchedule({ term, onAdd, onEdit, onDelete }: ImplFeeScheduleProps): ReactNode {
  const currency = term.impl_fee_currency;
  const tiers = [...term.impl_fee_tiers].sort(byCeiling);

  return (
    <div className={styles.tierPanel}>
      <div className={styles.tierHeader}>
        <span className={styles.tierTitle}>Implementation fee</span>
        {currency !== null && (
          <Badge shape="tag" tone="neutral">
            {currency}
          </Badge>
        )}
        {onAdd !== undefined && currency !== null && (
          <Button
            small
            variant="subtle"
            aria-label={`Add a fee band to the term effective ${term.effective_date}`}
            onClick={onAdd}
          >
            Add band
          </Button>
        )}
      </div>

      {currency === null ? (
        <p className={styles.tierEmpty}>
          This term prices no implementation fee. Set its fee currency to start a schedule — a band
          is an amount, and an amount has to say what money it is in.
        </p>
      ) : tiers.length === 0 ? (
        <p className={styles.tierEmpty}>
          No bands yet, so no deal under this term resolves an implementation fee.
        </p>
      ) : (
        <ul className={styles.tierList}>
          {tiers.map((tier, index) => {
            const band = bandLabel(tier, tiers[index - 1]?.max_accounts ?? null);
            return (
              <li key={tier.id} className={styles.tier}>
                <span className={styles.tierBand}>{band}</span>
                <span className={`${styles.tierFee} mono`}>
                  {formatMoney(tier.fee_minor, currency)}
                </span>
                <span className={styles.tierShare}>
                  {`our share ${formatPercent(term.impl_split_pct)} · `}
                  <span className="mono">
                    {formatMoney(splitMinor(tier.fee_minor, term.impl_split_pct), currency)}
                  </span>
                </span>
                {tier.notes !== null && tier.notes !== "" && (
                  <span className={styles.tierNotes}>{tier.notes}</span>
                )}
                {(onEdit !== undefined || onDelete !== undefined) && (
                  <span className={styles.tierActions}>
                    {onEdit !== undefined && (
                      <Button
                        small
                        variant="subtle"
                        aria-label={`Edit the ${band} band`}
                        onClick={() => onEdit(tier)}
                      >
                        Edit
                      </Button>
                    )}
                    {onDelete !== undefined && (
                      <Button
                        small
                        variant="subtle"
                        aria-label={`Delete the ${band} band`}
                        onClick={() => onDelete(tier)}
                      >
                        Delete
                      </Button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Ascending ceiling with the open-ended band last — the order the resolution
 * walks the bands in (`order by max_accounts asc nulls last`), so the list reads
 * as the sequence a customer is matched against rather than as an arbitrary
 * permutation of it. It is also what makes each band's lower edge the previous
 * row's ceiling, which is how `bandLabel` describes a range it does not store.
 */
function byCeiling(a: AgreementImplFeeTier, b: AgreementImplFeeTier): number {
  if (a.max_accounts === null) return b.max_accounts === null ? 0 : 1;
  if (b.max_accounts === null) return -1;
  return a.max_accounts - b.max_accounts;
}
